import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PaymentPlans } from "../../../Models/Credit";
import { PaymentType } from "../../../Models/PaymentType";
import { RefCodeType } from "../../../Models/ReferenceCodes";
import { ROUTE_PATHS } from "../../../Routes/constants";
import { bulkAndSinglePaymentLinkGenerate } from "../../../Services/BankService";
import { sendEvent } from "../../../Utils/analytics";
import { getRemoteValue } from "../../../Utils/firebase";
import useAuthState from "../../../Utils/store";
import AllDebts from "../../AllDebts/AllDebts";
import CLoadingButton from "../../CLoadingButton/CLoadingButton";


export interface MasterpassTabProps {
  isAllPayment: boolean;
}

const MasterpassTab = ({ isAllPayment }: MasterpassTabProps) => {
  const { paymentMethod, user, previewSummary } = useAuthState();
  const setPaymentMethod = useAuthState((state) => state.setPaymentMethod);
  const setIsAllPayment = useAuthState((state) => state.setIsAllPayment);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const whiteListControl = user && user?.features?.MASTERPASS_WHITELIST;

  const [linkGenerating, setLinkGenerating] = useState<boolean>(false);

  const location = useLocation();
  const [snoozeDetails] = useState(location.state); // useState ile yönetilmeli

  const masterpassValidPaymentTypes = paymentMethod?.type === RefCodeType.SPECIFIC || paymentMethod?.type === RefCodeType.SNOOZE;

  const handleSinglePayment = async (creditId: number) => {
    setLoading(true);
    try {
      const response = await bulkAndSinglePaymentLinkGenerate({
        type: PaymentType.SINGLE,
        creditId: creditId
      }
      );
      if (response) {
        navigate(ROUTE_PATHS.PAYMENT_FRAME, {
          state: {
            url: response.url, token: response.token, type: PaymentType.SINGLE
          }
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkGenerateClick = async () => {

    // CRL-3241 "Toplu Öde / Tutar gir": a user-entered bulk amount is paid as CUSTOM
    const customAmount = paymentMethod?.type === RefCodeType.ALL ? paymentMethod?.amount : undefined;
    const paymentAmount = paymentMethod?.type === 3 ? snoozeDetails?.snoozeFee : customAmount ?? previewSummary?.amount;
    const paymentType = paymentMethod?.type === 1
      ? PaymentType.LATE
      : customAmount !== undefined && customAmount > 0
        ? PaymentType.CUSTOM
        : PaymentType.ALL;

    setLinkGenerating(true);

    const response = await bulkAndSinglePaymentLinkGenerate({ type: paymentType, amount: paymentAmount, creditId: paymentMethod?.creditId });
    setLinkGenerating(false);

    if (response) {
      navigate(ROUTE_PATHS.PAYMENT_FRAME, {
        state: {
          url: response.url,
          token: response.token,
          referrer: ROUTE_PATHS.CHECKOUT,
          type: paymentType,
        }
      });
    }
  };

  const handleMasterpassRedirect = async () => {
    const paymentType = paymentMethod?.type || RefCodeType.ALL;
    if (paymentType == RefCodeType.RECURRING) {
      sendEvent(`click_recurring_masterpass_button`);
    } else if (paymentType == RefCodeType.SNOOZE) {
      sendEvent(`click_make_snooze_payment_button`, {
        amount: paymentMethod?.snoozeDetails?.snoozeFee,
        snoozedDay: paymentMethod?.snoozeDetails?.snoozeDays
      });
    } else {
      sendEvent(`click_pay_masterpass_button`);
    }
    setLoading(true);
    if (paymentMethod?.creditId) {
      const paymentType = paymentMethod?.type === RefCodeType.SNOOZE ? PaymentType.SNOOZE : PaymentType.SINGLE;
      const response = await bulkAndSinglePaymentLinkGenerate({
        type: paymentType,
        creditId: paymentMethod!.creditId,
        snoozeDays: paymentMethod?.type === RefCodeType.SNOOZE ? paymentMethod?.snoozeDetails?.snoozeDays : undefined
      }
      ).finally(() => {
        setLoading(false);
      });
      if (response) {
        navigate(ROUTE_PATHS.PAYMENT_FRAME, {
          state: {
            url: response.url,
            token: response.token,
            type: paymentType,
          }
        });
      }
    }
  };

  useEffect(() => {
    sendEvent(`view_checkout_card_tab`, {
      total_payment_amount: previewSummary?.amount,
    });
  }, []);

  const [isSinglePayment, setIsSinglePayment] = useState<boolean>(false);

  const handlePaymentsResponse = (payments: PaymentPlans) => {
    if (payments.meta?.total == 1) {
      setPaymentMethod({
        type: paymentMethod?.type == RefCodeType.LATE ? RefCodeType.LATE : RefCodeType.SPECIFIC,
        creditId: payments.data[0].creditId,
        snoozeDetails: paymentMethod?.snoozeDetails
      });
      setIsSinglePayment(true);
      setIsAllPayment(false);
      setLoading(false);
      return true;
    }
    return false;
  }

  if (isAllPayment && !isSinglePayment) {
    return (<Stack
      sx={{
        borderRadius: "16px",
        border: "1px solid #F1F5F9",
        boxShadow: "0px 4px 17px 0px #F1F1F4",
      }}
      className="p-4 md:p-6 mt-6"
    >
      <div className="text-[15px] md:text-[16px] font-semibold text-[#475569]">
        {getRemoteValue("credit_debt_pay")}
      </div>

      <CLoadingButton
        fullWidth
        loading={linkGenerating}
        sx={{ mt: 3, mb: 2 }}
        size="large"
        variant="contained"
        onClick={handleLinkGenerateClick}
      >
        {getRemoteValue("pay")}
      </CLoadingButton>
      <div className="text-[12px] md:text-[14px] text-[#333] flex flex-col p-3 bg-[#FAFAFB] rounded-lg">
        {getRemoteValue("masterpass_desc")}
      </div>

    </Stack>
    )
  }

  return (
    <>

      {(isAllPayment && !isSinglePayment) ? (
        <AllDebts
          currentTab={0}
          onPayClick={handleSinglePayment}
          isHomePage={false}
          gridCount={2}
          onDataFetched={handlePaymentsResponse}
        />
      ) : (
        <Stack
          sx={{
            borderRadius: "16px",
            border: "1px solid #F1F5F9",
            boxShadow: "0px 4px 17px 0px #F1F1F4",
          }}
          className="p-4 md:p-6 mt-6"
        >
          {!isSinglePayment &&
            (
              <div className="text-[15px] md:text-[16px] font-semibold text-[#475569] flex">
                {getRemoteValue("credit_debt_pay")}
                {(!masterpassValidPaymentTypes || !whiteListControl) && (
                  <img
                    src={`/icons/soon-icon.svg`}
                    alt="debt-imagap"
                    style={{ marginLeft: "8px" }}
                  />
                )}
              </div>
            )

          }

          {isSinglePayment && (
            <div className="text-[15px] md:text-[16px] font-semibold text-[#475569] flex">
              {getRemoteValue("credit_debt_pay")}
            </div>
          )}


          {(isSinglePayment || (masterpassValidPaymentTypes && whiteListControl)) && (
            <CLoadingButton
              fullWidth
              type="submit"
              variant="contained"
              sx={{ mt: 4 }}
              loading={loading}
              onClick={handleMasterpassRedirect}
            >
              {getRemoteValue("pay")}
            </CLoadingButton>
          )}


          <Stack
            sx={{ borderRadius: "12px", background: " #FAFAFB" }}
            className="mt-4 md:mt-6"
          >
            <p
              className="xs:text-sm md:text-base font-normal p-4"
              dangerouslySetInnerHTML={{
                __html: isSinglePayment || (masterpassValidPaymentTypes && whiteListControl)
                  ? getRemoteValue("masterpass_desc")
                  : getRemoteValue("credit_debt_pay_soon"),
              }}
            />
          </Stack>
        </Stack>
      )}
    </>
  );
};

export default MasterpassTab;
